import type { DeletionDeadlineInfoDto } from 'cadenza/api-client/dto/deletion-deadline-dto';
import type { TextDataViewAttributeValueDto } from 'cadenza/api-client/dto/text-data-view-attribute-value-dto';
import type { MediaItemPropertyName } from 'cadenza/repository/model/media-item-property-name';
import type { MediaItemDto } from 'cadenza/repository/model/media-item-dto';
import type { TableDataDto } from 'cadenza/ui/base-table/model/table-data-dto';
import type { TableConfigurationDto } from 'cadenza/result-table/table-configuration-dto';
import { ajax } from 'cadenza/api-client/api';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import {
  HEADER_EXPORT_TYPE,
  HEADER_POLL_REQUEST,
  MIMETYPE_DISY_WORKORDER_ID,
  PARAMETER_MAP_RESOLUTION,
  PARAMETER_SRS_CODE
} from 'cadenza/api-client/api-constants';
import {
  type ImageMediaType,
  MIMETYPE_JSON,
  MIMETYPE_TEXT,
  MIMETYPE_ZIP
} from 'cadenza/model/media-type';
import { download } from 'cadenza/utils/download';
import { assert } from 'cadenza/utils/custom-error';
import type { DimensionDto } from 'cadenza/api-client/dto/dimension-dto';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import type {
  ObjectInfoAutoResolvedAttributesDto
} from 'cadenza/api-client/dto/object-info-auto-resolved-attributes-dto';
import { settingsJsonParser } from 'cadenza/utils/settings-json-parser';
import type { SaveWorkbookResult } from 'cadenza/api-client/workbook-api-util';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import type { ObjecttypeId, ObjecttypeIdString } from 'cadenza/repository/objecttype-id';
import type { RepositoryName } from 'cadenza/repository/model/repository-name';
import type { WorkbookIdAndHash } from 'cadenza/workbook/api/workbook-id-and-hash';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { WorkbookKey } from 'cadenza/workbook/api/workbook-key';
import type { WorkbookId } from 'cadenza/workbook/api/workbook-id';
import type { ConditionId } from 'cadenza/workbook/model/condition-id';
import type { ObjectInfoElementsDto } from 'cadenza/workbook/model/object-info-elements-dto';
import type { DownstreamOperationDto } from 'cadenza/selfservice/import-data/api/operation/downstream-operation-dto';
import { Command, isCommandType } from 'cadenza/api-client/workbook-commands';
import type {
  WorkbookJobScheduleConfiguration
} from 'cadenza/workbook/worksheet-subscription/worksheet-subscription-wizard/workbook-job-schedule-configuration';
import type { ChartViewDataDto } from 'cadenza/api-client/dto/chart-view-data-dto';
import { OL_DEFAULT_MAP_SRS } from 'cadenza/acquisition/acquisition-layer-utils';
import type { LegendItemDto } from 'cadenza/map/dto/LegendItemDto';
import type { ProjectIdTuple } from 'cadenza/repository/model/project-id';
import type { TableConfigurationWithOriginDto } from 'cadenza/workbook/tables/workbook-table-view';
import type { UploadedFileDto } from 'cadenza/api-client/dto/uploaded-file-dto';
import { toSearchParams } from 'cadenza/api-client/dto/uploaded-file-dto';
import type { ChangesRequestDto } from 'cadenza/api-client/dto/changes-request-dto';
import type { VisualizationDto } from 'cadenza/workbook/model/visualization-dto';
import type { VisualizationCategory } from 'cadenza/workbook/model/visualization-category';
import type { ProjectName } from 'cadenza/repository/model/project-name';
import type { DataBrowserDto } from 'cadenza/workbook/model/data-browser-dto';
import type { Settings } from 'cadenza/workbook/workbook-settings-util';
import type { WorkbookVersion } from 'cadenza/workbook/api/workbook-version';
import type { VersionMode } from 'cadenza/repository/version-mode';
import type { DataProtectionContextIdTuple } from 'cadenza/repository/model/data-protection-context-id';
import type { IndicatorViewDto } from 'cadenza/api-client/dto/indicator-view-dto';
import type { WorkbookMetadataDto } from 'cadenza/api-client/dto/workbook-metadata-dto';
import type { ObjecttypeDetailsDto } from 'cadenza/api-client/dto/objecttype-details-dto';
import type { TableData } from 'cadenza/api-client/tables-api';
import type { AttributeValueRowDto } from 'cadenza/api-client/dto/attribute-value-row-dto';

import type { Operation } from 'fast-json-patch';
import type { GeoJSONMultiPolygon } from 'ol/format/GeoJSON';

/**
 * Prevent the given workbook from being removed from the workbook graph.
 *
 * @param workbookIdAndHash - Id and hash of the workbook to keep alive
 * @return A promise for whether the requests was successful
 */
export function keepWorkbookAlive (workbookIdAndHash: WorkbookIdAndHash) {
  return ajax(createWorkbookUrl(workbookIdAndHash), {
    method: 'HEAD',
    headers: { [HEADER_POLL_REQUEST]: 'poll' } // do not keep the session alive, and set a dummy value in the header since empty headers are illegal
  });
}

/**
 * Returns visualizations.
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param dataViewId - Id of the data view
 * @return Promise containing visualizations
 */
export function getVisualizations (workbookIdAndHash: WorkbookIdAndHash, dataViewId?: DataViewId) {
  return ajax<Record<VisualizationCategory, VisualizationDto[]>>(createWorkbookUrl(workbookIdAndHash, { dataViewId }) + '/visualizations', { dataType: 'json' });
}

export function exportMultipleDataViewData (workbookIdAndHash: WorkbookIdAndHash, dataViewId: DataViewId[], mediaType: string, uploadedFile?: UploadedFileDto) {
  let url = `/workbooks/${createWorkbookParticle(workbookIdAndHash)}/dataViews;dataViewIds=`;

  // concatenate comma seperated dataViewIds to url
  for (let i = 0; i < dataViewId.length; i++)  {
    url += dataViewId[i];
    if (i === dataViewId.length - 1) {
      url += '/export-work-orders';
    } else {
      url += ',';
    }
  }

  if (uploadedFile) {
    url = url + `?${toSearchParams(uploadedFile)}`;
  }
  return ajax<WorkOrderId>(
    url,
    {
      method: 'POST',
      headers: { [HEADER_EXPORT_TYPE]: mediaType, 'Accept': MIMETYPE_DISY_WORKORDER_ID } });
}

export function checkIfDataViewDataCanBeExported (workbookIdAndHash: WorkbookIdAndHash, dataViewId: DataViewId, mediaType: string) {
  return ajax<WorkOrderId>(
    createWorkbookUrl(workbookIdAndHash, { dataViewId }),
    {
      method: 'OPTIONS',
      headers: { [HEADER_EXPORT_TYPE]: mediaType }
    });
}

export function exportDataViewData (workbookIdAndHash: WorkbookIdAndHash, dataViewId: DataViewId, mediaType: string, uploadedFile?: UploadedFileDto) {
  let url = createWorkbookUrl(workbookIdAndHash, { dataViewId });
  if (uploadedFile) {
    url = url + `?${toSearchParams(uploadedFile)}`;
  }
  return ajax<WorkOrderId>(
    url,
    { headers: { [HEADER_EXPORT_TYPE]: mediaType, 'Accept': MIMETYPE_DISY_WORKORDER_ID } });
}

/**
 * Checks whether object type is used in any workbook
 *
 * @param objecttypeId - {ObjecttypeId} - Object type id.
 * @return - Is object type shared with some workbook
 */
export function checkWhetherObjecttypeIsUsedInAnyWorkbook (objecttypeId: ObjecttypeId) {
  return ajax('/objecttypes/' + btoa(objecttypeId) + '/used-in-workbooks');
}

export interface TableViewDataOptions {
  /** index to start loading rows from */
  offset: number;
  /** number of rows to load */
  limit: number;
  /** request ID for cancelling that request */
  signal?: AbortSignal;
}

/**
 * Returns table data for the view.
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param viewId - Id of the view
 * @param parameters - Request parameters
 * @return Data of the table
 */
export function getTableViewData (workbookIdAndHash: WorkbookIdAndHash, viewId: ViewId, parameters: TableViewDataOptions) {
  const { offset, limit, signal } = parameters;
  const params = { offset: String(offset), limit: String(limit) };
  const baseUrl = createWorkbookUrl(workbookIdAndHash, { viewId }) + '/tableData?';
  const url = baseUrl + new URLSearchParams(params);
  return ajax<TableDataDto>(url, { dataType: 'json', signal });
}

interface IndividualValuesTableDataOptions {
  /** index to start loading rows from */
  offset: number;
  /** number of rows to load */
  limit: number;
  /** request ID for cancelling that request */
  signal?: AbortSignal;
  /** attribute name of a column to sort */
  orderByColumnName?: string;
  /** whether to sort in ascending order */
  orderAsc?: boolean;
}

/**
 * Returns table data for the individual values table. Table data is based on current workbook selection.
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param viewId - Id of the view
 * @param parameters - Request parameters
 * @return Data of the table
 */
export function getIndividualValuesTableData (workbookIdAndHash: WorkbookIdAndHash, viewId: ViewId, parameters: IndividualValuesTableDataOptions) {
  const { offset, limit, signal, orderByColumnName, orderAsc } = parameters;
  // convert the numbers to strings explicitly to avoid typescript problems
  const params = { offset: String(offset), limit: String(limit) };
  if (orderByColumnName) {
    Object.assign(params, { orderByColumnName, orderAsc });
  }
  const baseUrl = createWorkbookUrl(workbookIdAndHash, { viewId }) + '/individualValuesTableData?';
  const url = baseUrl + new URLSearchParams(params);
  return ajax<TableDataDto>(url, { dataType: 'json', signal });
}

/**
 * Returns chart data for the view.
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param viewId - Id of the view
 * @param signal - Signal for aborting the request
 * @return Data of the chart
 */
export function getChartViewData (workbookIdAndHash: WorkbookIdAndHash, viewId: ViewId, signal: AbortSignal): Promise<ChartViewDataDto> {
  return ajax(createWorkbookUrl(workbookIdAndHash, { viewId }) + '/chartData', { dataType: 'json', signal });
}

/**
 * Returns table configuration for the view.
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param viewId - Id of the view
 * @param signal - Signal for aborting the request
 * @return Setting of the table
 */
export function getTableViewConfiguration (workbookIdAndHash: WorkbookIdAndHash, viewId: ViewId, signal: AbortSignal) {
  return ajax<TableConfigurationWithOriginDto>(createWorkbookUrl(workbookIdAndHash, { viewId }) + '/tableConfiguration', {
    dataType: 'json',
    signal
  });
}

/**
 * Returns table configuration for the individual values table.
 * Table configuration is based on current workbook selection.
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param viewId - Id of the view
 * @param signal - Signal for aborting the request
 * @return Setting of the table
 */
export function getIndividualValuesTableConfiguration (workbookIdAndHash: WorkbookIdAndHash, viewId: ViewId, signal: AbortSignal) {
  return ajax<TableConfigurationDto>(createWorkbookUrl(workbookIdAndHash, { viewId }) + '/individualValuesTableConfiguration', { dataType: 'json', signal });
}

/**
 * Checks if currently visible drillThrough result can be downloaded with specified mimeType.
 * Check takes into account export limits configured in the system.
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param viewId - Id of the view
 * @param mediaType - MIME type of expected result
 * @return Export check result
 */
export function checkIfIndividualValuesTableCanBeExported (workbookIdAndHash: WorkbookIdAndHash, viewId: ViewId, mediaType: string) {
  return ajax(
    createWorkbookUrl(workbookIdAndHash, { viewId }) + '/individualValues',
    {
      method: 'OPTIONS',
      headers: { [HEADER_EXPORT_TYPE]: mediaType }
    });
}

export interface IndividualValuesTableDataExportOptions {
  orderByColumnName?: string;
  orderAsc?: boolean;
  targetFile?: UploadedFileDto;
}

/**
 * Starts download of currently visible drillThrough result. Result will be prepared accordingly to provided mimeType.
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param viewId - Id of the view
 * @param mediaType - MIME type of expected result
 * @param parameters - Request parameters
 * @param [parameters.orderByColumnName] - Attribute of a column to sort
 * @param [parameters.orderAsc] - Whether to sort in ascending order
 * @param [parameters.targetFile] - Details of target file to append data to
 * @return Export result
 */
export function exportIndividualValuesTableData (workbookIdAndHash: WorkbookIdAndHash, viewId: ViewId, mediaType: string, { orderByColumnName, orderAsc, targetFile }: IndividualValuesTableDataExportOptions = {}) {
  const baseUrl = createWorkbookUrl(workbookIdAndHash, { viewId });
  let data = orderByColumnName ? { orderByColumnName, orderAsc } : {};
  if (targetFile) {
    data = Object.assign(data, targetFile);
  }
  const headers = { Accept: MIMETYPE_DISY_WORKORDER_ID, [HEADER_EXPORT_TYPE]: mediaType };
  return ajax<WorkOrderId>(`${baseUrl}/individualValues`, { data, headers });
}

/**
 * Returns displayed value for the view.
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param viewId - Id of the view
 * @param signal - Signal for aborting the request
 * @return Data of the indicator
 */
export function getIndicatorViewData (workbookIdAndHash: WorkbookIdAndHash, viewId: ViewId, signal: AbortSignal) {
  return ajax<IndicatorViewDto>(createWorkbookUrl(workbookIdAndHash, { viewId }) + '/indicatorData', { dataType: 'json', signal });
}

/**
 * Returns a list of rows matching to active attributes used in the given text view.
 *
 * @param workbookIdAndHash
 * @param viewId
 * @param signal
 */
export function getTextViewData (workbookIdAndHash: WorkbookIdAndHash, viewId: ViewId, signal: AbortSignal) {
  return ajax <Record<ActiveAttributeId, TextDataViewAttributeValueDto>>(createWorkbookUrl(workbookIdAndHash, { viewId }) + '/text-view-data', { dataType: 'json', signal });
}

interface CommandDto<P = object> {
  /** the type of the command as {string} */
  type: string;

  /** payload The data to send with the command */
  payload: P;
}

export interface GetPartialWorkbookStateResult {
  workbookIdAndHash: WorkbookIdAndHash;
  operations: Operation[];
}

export interface ExecuteCommandResult extends GetPartialWorkbookStateResult {
  invalidViewsOrDataViews: (ViewId | DataViewId)[];
  invalidLayerIdsByViewId: Record<ViewId, LayerId[]>;
  dataViewsAffectedBySelectionChange: DataViewId[];
  viewsToZoomToDataExtent: ViewId[];
}

export interface GetWorkbookStateResult extends ExecuteCommandResult {
  unavailableObjecttypeIds: ObjecttypeIdString[];
  version: WorkbookVersion;
  versionMode: VersionMode;
}

function commandToDto (command: Command): CommandDto {
  const commandDto: CommandDto<{ settings?: Settings }> = {
    type: command.type,
    payload: { ...command.payload }
  };
  if (commandDto.payload.settings) {
    commandDto.payload.settings = stringifySettingsValues(commandDto.payload.settings);
  } else if (isCommandType(command, Command.COMPOSITE_COMMAND)) {
    (commandDto.payload as { commands: CommandDto[] }).commands = command.payload.commands.map(commandToDto);
  }
  return commandDto;
}

// exported only for testing purposes
export { commandToDto as __commandToDto__ };

function stringifySettingsValues (settings: Settings) {
  const result: Settings = {};
  Object.entries(settings).forEach(([ key, value ]) => {
    if (!key.endsWith('.default')) {
      result[key] = (value == null || typeof value === 'string') ? value : JSON.stringify(value);
    }
  });
  return result;
}

/**
 * Executes the given command.
 *
 * @param workbookIdAndHash - The state of the id and state of the workbook to extend from
 * @param command - The command to execute
 * @return Object reflecting the workbook model sparsely filled with only the changes
 */
export function executeCommand (workbookIdAndHash: WorkbookIdAndHash, command: Command) {
  assert(command, 'missing command');
  assert(command.type, 'missing command type');
  assert(command.payload, 'missing command payload');
  return ajax<ExecuteCommandResult>(createWorkbookUrl(workbookIdAndHash) + '/commands', {
    method: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(commandToDto(command)),
    converters: { 'text json': settingsJsonParser }
  });
}

/**
 * Get workbook state via hash.
 *
 * @param workbookIdAndHash - Id and hash of the workbook to revert state
 * @return workbook state for worksheets, conditions and hierarchies
 */
export function getWorkbookState (workbookIdAndHash: WorkbookIdAndHash) {
  return ajax<GetWorkbookStateResult>(createWorkbookUrl(workbookIdAndHash), {
    headers: { 'Accept': MIMETYPE_JSON },
    converters: { 'text json': settingsJsonParser }
  });
}

export function getPartialWorkbookState (workbookIdAndHash: WorkbookIdAndHash, changesRequestDto: ChangesRequestDto) {
  return ajax<GetPartialWorkbookStateResult>(createWorkbookUrl(workbookIdAndHash), {
    type: 'POST',
    data: JSON.stringify(changesRequestDto),
    contentType: MIMETYPE_JSON
  });
}

export function getObjectTypesAndDataSourcesVisibleInWorkbooks (projectId?: ProjectIdTuple) {
  let url = '/objecttypes';
  if (projectId) {
    const [ repositoryName, projectName ] = projectId;
    const urlParams = new URLSearchParams({ repositoryName, projectName });
    url += `?${urlParams}`;
  }
  return  ajax<DataBrowserDto>(url, {
    headers: { 'Accept': MIMETYPE_JSON },
    converters: { 'text json': settingsJsonParser }
  });
}

/**
 * Determine objecttype details like is deletable or in a writableRepository
 *
 * @param objecttypeId - the id of the objecttype with name and repository
 * @return details - requested details
 */
export function getObjecttypeSelfServiceDetails (objecttypeId: ObjecttypeId) {
  return ajax<ObjecttypeDetailsDto>(`/selfservice/objecttypes/${window.btoa(objecttypeId)}`, {
    type: 'GET',
    headers: { 'Accept': MIMETYPE_JSON }
  }).catch(error => {
    throw new Error(`An error occurred while trying to check whether objecttype is deletable: ${error}`);
  });
}

export function getMediaItems (
  workbookIdAndHash: WorkbookIdAndHash,
  mediaTypes: ImageMediaType[],
  properties: Partial<Record<MediaItemPropertyName, string>>
) {
  const urlParams = new URLSearchParams({ mediaTypes: mediaTypes.join(), ...properties });
  return ajax<MediaItemDto[]>(`${createWorkbookUrl(workbookIdAndHash)}/media-items?${urlParams}`, {
    type: 'GET',
    headers: { 'Accept': MIMETYPE_JSON }
  }).catch(error => {
    throw new Error(`An error occurred while trying to retrieve MediaItems: ${error}`);
  });
}

export function geocodeObjecttypeUnstructured (objecttypeId: ObjecttypeId, parameters: DownstreamOperationDto): Promise<false | WorkOrderId> {
  return ajax<WorkOrderId>(`/operation/unstructuredGeocoding/${btoa(objecttypeId)}`, {
    type: 'POST',
    data: JSON.stringify(parameters),
    contentType: MIMETYPE_JSON
  }).catch(error => {
    throw new Error(`An error occurred while trying to geocode objecttype: ${error}`);
  });
}

export function geocodeObjecttypeStructured (objecttypeId: ObjecttypeId, parameters: DownstreamOperationDto): Promise<false | WorkOrderId> {
  return ajax<WorkOrderId>(`/operation/structuredGeocoding/${btoa(objecttypeId)}`, {
    type: 'POST',
    data: JSON.stringify(parameters),
    contentType: MIMETYPE_JSON
  }).catch(error => {
    throw new Error(`An error occurred while trying to geocode objecttype: ${error}`);
  });
}

export function geocodeIpObjecttype (objecttypeId: ObjecttypeId, parameters: DownstreamOperationDto): Promise<false | WorkOrderId> {
  return ajax<WorkOrderId>(`/operation/geocoderIp/${btoa(objecttypeId)}`, {
    type: 'POST',
    data: JSON.stringify(parameters),
    contentType: MIMETYPE_JSON
  }).catch(error => {
    throw new Error(`An error occurred while trying to ip geocode objecttype: ${error}`);
  });
}

export function getCompatibleObjectTypesForSelfService (workbookIdAndHash: WorkbookIdAndHash, viewId: ViewId, dataType: string, srs: string) {
  const urlParams = new URLSearchParams({ dataType, srs });
  if (viewId)  {
    urlParams.append('usedInView', viewId);
  }
  return ajax(createWorkbookUrl(workbookIdAndHash)
    + `/objecttypes?${urlParams}`, {
    type: 'GET',
    headers: { 'Accept': MIMETYPE_JSON }
  });
}

export interface ConditionTableOptions {
  /** index to start loading rows from */
  offset?: number;
  /** number of rows to load */
  limit?: number;
  filterType?: string;
  /** text for filtering results that contain that text */
  filterText?: string;
  /** comma-separated list of columns to filter by */
  filterByColumn?: string;
  /** enables execution of the condition table query in context insensitive mode */
  forceContextInsensitiveMode?: boolean;
  orderAsc?: boolean;
  /** index of the column to order the rows by */
  orderByColumn?: number;
  noCount?: boolean;
  /** abort controller signal so the requests can be cancelled */
  signal?: AbortSignal;
}

/**
 * Returns condition table.
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param worksheetId - Id of the active worksheet
 * @param conditionId - Id of the condition
 * @param parameters - Request parameters
 * @return Data of the table
 */
export function getWorkbookConditionTable (
  workbookIdAndHash: WorkbookIdAndHash,
  worksheetId: WorksheetId,
  conditionId: ConditionId,
  parameters: ConditionTableOptions) {
  const {
    offset = 0,
    limit = -1,
    filterText = '',
    /** no default sorting by first column - default sorting will be calculated in the backend from the attribute settings */
    orderByColumn = -1,
    signal = undefined,
    filterType = 'SUBSTRING',
    filterByColumn = '',
    orderAsc,
    forceContextInsensitiveMode = false,
    noCount = false
  } = parameters;
  const baseUrl = createWorkbookUrl(workbookIdAndHash) + `/conditions/${conditionId}/table?`;
  let url = baseUrl + new URLSearchParams({
    activeInWorksheet: worksheetId,
    offset: String(offset),
    limit: String(limit),
    filterText,
    filterType,
    filterByColumn,
    orderByColumn: String(orderByColumn),
    forceContextInsensitiveMode: String(forceContextInsensitiveMode),
    noCount: String(noCount) });
  if (orderAsc !== undefined) {
    url = `${url}&orderAsc=${orderAsc}`;
  }
  return ajax<TableDataDto>(url, { dataType: 'json', signal });
}

/**
 * Returns values of spatial condition including geometries for each shape. (not the merged shape)
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param conditionId - Id of the condition
 * @return Condition values
 */
export function getSpatialConditionValues (workbookIdAndHash: WorkbookIdAndHash, conditionId: ConditionId) {
  const url = createWorkbookUrl(workbookIdAndHash) + `/conditions/${conditionId}/values`;
  return ajax(url, { dataType: 'json' });
}

export function getResolvedSpatialConditionValuesAsMultiPolygon (workbookIdAndHash: WorkbookIdAndHash, conditionId: ConditionId, targetSrs: string): Promise<GeoJSONMultiPolygon> {
  const url = createWorkbookUrl(workbookIdAndHash) + `/conditions/${conditionId}/resolved-multi-polygon?targetSrs=${targetSrs}`;
  return ajax(url, { dataType: 'json' });
}

/**
 * Returns values of spatial condition including geometries from the items of the current selection.
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @return Condition values
 */
export function getSpatialConditionValueFromSelection (workbookIdAndHash: WorkbookIdAndHash) {
  return ajax(`${createWorkbookUrl(workbookIdAndHash)}/selection/geometries`, {
    method: 'GET',
    contentType: MIMETYPE_JSON
  });
}

/**
 * Returns the object information from the items of the current selection.
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param mapSrs - map srs
 * @param mapResolution - current resolution of the map view
 * @param signal - ?
 * @return The object info
 */
export function getObjectInfo (workbookIdAndHash: WorkbookIdAndHash, mapSrs: string, mapResolution: number, signal?: AbortSignal) {
  const searchParams = new URLSearchParams({
    [PARAMETER_SRS_CODE]: mapSrs ?? OL_DEFAULT_MAP_SRS,
    [PARAMETER_MAP_RESOLUTION]: String(mapResolution)
  });
  return ajax<ObjectInfoElementsDto[]>(`${createWorkbookUrl(workbookIdAndHash)}/selection/objects?${searchParams}`, {
    method: 'GET',
    contentType: MIMETYPE_JSON,
    signal
  });
}

/**
 * Returns all the attributes that can be automatically calculated and shown to user when displaying object info..
 *
 * @param workbookIdAndHash - Id and hash of the workbook
 * @param geometryAttribute - Id of the geometry attribute
 * @param hierarchyLevel - hierarchy level
 * @return Dto containing all the possible attributes shown in feature info.
 */
export function getAllObjectInfoAttributes (workbookIdAndHash: WorkbookIdAndHash, geometryAttribute?: ActiveAttributeId, hierarchyLevel?: string) {
  const searchParams = new URLSearchParams();
  if (geometryAttribute) {
    searchParams.set('geometryAttribute', geometryAttribute);
  }
  if (hierarchyLevel) {
    searchParams.set('hierarchyLevel', hierarchyLevel);
  }
  return ajax<ObjectInfoAutoResolvedAttributesDto>(`${createWorkbookUrl(workbookIdAndHash)}/object-info/attributes?${searchParams}`, {
    method: 'GET',
    contentType: MIMETYPE_JSON
  });
}

export function getWorkbookConditionContextSensitiveData (
  workbookIdAndHash: WorkbookIdAndHash,
  worksheetId: WorksheetId,
  conditionId: ConditionId) {
  return getWorkbookConditionTable(
    workbookIdAndHash,
    worksheetId,
    conditionId,
    { offset: 0, limit: -1, orderByColumn: -1 });
}

/**
 * Creates the URL path component that represents workbook id and hash.
 *
 * @param workbookIdAndHash - id and hash
 * @return path component to address workbook at hash
 */
export function createWorkbookParticle (workbookIdAndHash: WorkbookIdAndHash) {
  return `${workbookIdAndHash.id},hash=${workbookIdAndHash.hash}`;
}

interface WorkbookUrlOptions {
  worksheetId?: WorksheetId;
  viewId?: ViewId;
  dataViewId?: DataViewId;
}

/**
 * Create the workbook part of the workbook URL based on the given settings.
 *
 * @param workbookIdAndHash - ID + hash or repository name.
 * @param [options] - set of additional options.
 * @param [options.worksheetId] - ID of worksheet to navigate to.
 * @param [options.viewId] - ID of view to activate.
 * @param [options.dataViewId] - ID of data view to activate.
 * @return URL component for workbook part.
 */
export function createWorkbookUrl (workbookIdAndHash: WorkbookIdAndHash, { worksheetId, viewId, dataViewId }: WorkbookUrlOptions = {}) {
  assert(
    Number(!worksheetId) + Number(!viewId) + Number(!dataViewId) >= 2,
    'Only one of worksheetId, viewId and dataViewId can be given at a time');
  let url = `/workbooks/${createWorkbookParticle(workbookIdAndHash)}`;
  if (worksheetId) {
    url += `/worksheets/${worksheetId}`;
  } else if (viewId) {
    url += `/views/${viewId}`;
  } else if (dataViewId) {
    url += `/dataViews/${dataViewId}`;
  }
  return url;
}

/**
 * Create the workbook part of the workbook URL based on the given settings.
 *
 * @param key - repositoryName + ID.
 * @param [options] - set of additional options.
 * @param [options.worksheetId] - ID of worksheet to navigate to.
 * @param [options.viewId] - ID of view to activate.
 * @param [options.dataViewId] - ID of data view to activate.
 * @return URL component for workbook part.
 */
export function createWorkbookUrlByKey (key: WorkbookKey, { worksheetId, viewId, dataViewId }: WorkbookUrlOptions = {}) {
  assert(
    Number(!worksheetId) + Number(!viewId) + Number(!dataViewId) >= 2,
    'Only one of worksheetId, viewId and dataViewId can be given at a time');
  let url = `/repositories/${key.repositoryName}/workbooks/${key.id}`;
  if (worksheetId) {
    url += `/worksheets/${worksheetId}`;
  } else if (viewId) {
    url += `/views/${viewId}`;
  } else if (dataViewId) {
    url += `/dataViews/${dataViewId}`;
  }
  return url;
}

const workbookUrlRegExp = new RegExp('/workbooks/(?<id>[0-9A-Za-z-_]+),hash=(?<hash>[0-9A-Za-z-_]+=*)(/worksheets/(?<worksheetId>[0-9A-Za-z-_]+)|/views/(?<viewId>[0-9A-Za-z-_]+)|/dataViews/(?<dataViewId>[0-9A-Za-z-_]+)(?:/.+)?)?');

/**
 * Returns the object that contains parsed information from the workbook url.
 *
 * @param url - url
 * @return
 */
export function parseWorkbookUrl (url: string) {
  const match = url.match(workbookUrlRegExp);
  if (match) {
    const { id, hash, worksheetId, viewId, dataViewId } = match.groups!;
    return {
      workbookIdAndHash: { id: id as WorkbookId, hash: hash as string },
      worksheetId: worksheetId as WorksheetId,
      viewId: viewId as ViewId,
      dataViewId: dataViewId as DataViewId };
  }
}

/** see net/disy/cadenza/workbook/web/dto/SaveWorkbookDto.java */
export interface SaveWorkbookDto {
  name: string;
  folderItemName: string;
  labels: string[];
  displayState: DisplayState;
  comment: string;
  projectName?: ProjectName;
  dataProtectionContextId?: DataProtectionContextIdTuple;
  deletionDeadlineInfo?: DeletionDeadlineInfoDto;
  workbookIdAndHash: WorkbookIdAndHash;
}

/** see net/disy/cadenza/workbook/web/dto/WorkbookDisplayStateDto.java */
export interface DisplayState {
  isAnalysisContextDisplayed: boolean;
  isDataBrowserDisplayed: boolean;
  isDesignerDisplayed: boolean;
}

export function saveWorkbook (repositoryName: RepositoryName, options: SaveWorkbookDto) {
  return ajax<WorkOrderId>(`/repositories/${repositoryName}/create-workbook-work-orders`, {
    type: 'POST', data: JSON.stringify(options), contentType: MIMETYPE_JSON
  });
}

export function retrieveWorkbookMetadata (uploadedFile: UploadedFileDto) {
  return ajax<WorkbookMetadataDto>('/workbooks/import/metadata', {
    type: 'POST', data: JSON.stringify(uploadedFile), contentType: MIMETYPE_JSON
  });
}

export function saveWorkbookVersion (repositoryName: RepositoryName, workbookIdAndHash: WorkbookIdAndHash, { comment, displayState }: { comment?: string; displayState: DisplayState }) {
  const data = JSON.stringify({ workbookIdAndHash, comment, displayState });
  return ajax<SaveWorkbookResult>(`/repositories/${repositoryName}/workbooks/${workbookIdAndHash.id}/versions`, {
    type: 'POST', data, contentType: MIMETYPE_JSON
  });
}

export const updateVersionComment = (workbookId: WorkbookId, repositoryName: RepositoryName, version: number, comment: string) => {
  return ajax<void>(`/repositories/${repositoryName}/workbooks/${workbookId}/versions/${version}/comment`, {
    type: 'PUT', data: comment
  });
};

export const deleteWorkbookVersion = (workbookId: WorkbookId, repositoryName: RepositoryName, version: number) => {
  return ajax<void>(`/repositories/${repositoryName}/workbooks/${workbookId}/versions/${version}`, { type: 'DELETE' });
};

export const publishWorkbookVersion = (workbookId: WorkbookId, repositoryName: RepositoryName, version: number) => {
  return setWorkbookVersionPublished(repositoryName, workbookId, version, true);
};

export const unpublishWorkbookVersion = (workbookId: WorkbookId, repositoryName: RepositoryName, version: number) => {
  return setWorkbookVersionPublished(repositoryName, workbookId, version, false);
};

function setWorkbookVersionPublished (repositoryName: RepositoryName, workbookId: WorkbookId, version: number, published: boolean) {
  return ajax<void>(`/repositories/${repositoryName}/workbooks/${workbookId}/versions/${version}/published`, {
    type: 'PUT',
    data: Boolean(published).toString(),
    contentType: MIMETYPE_JSON
  });
}

export const isWorkbookPublishable = (workbookId: WorkbookId, repositoryName: RepositoryName, version: number) => {
  return ajax<boolean>(`/repositories/${repositoryName}/workbooks/${workbookId}/versions/${version}/publishable`);
};

export const renameWorkbook = (workbookKey: WorkbookKey, newName: string) => {
  return ajax<WorkbookIdAndHash>(`/repositories/${workbookKey.repositoryName}/workbooks/${workbookKey.id}/name`, {
    type: 'PUT',
    data: newName,
    contentType: MIMETYPE_TEXT
  });
};

/**
 * Download the given workbook as a ZIP file.
 *
 * @param obj
 * @param obj.id - The workbook id that should be downloaded.
 * @param [obj.hash] - The hash is given if workbook is opened already (not necessarily saved in repository).
 * @param [obj.repositoryName] - The repositoryName is given if we download from navigation menu.
 * @return
 */
export const downloadWorkbook = ({ id, hash, repositoryName }: { id: WorkbookId; hash?: string; repositoryName?: RepositoryName }) => {
  // hash is given if workbook is opened already (not necessarily saved in repository)
  // repositoryName is given if we download from navigation menu
  const url = repositoryName
    ? `/repositories/${repositoryName}/workbooks/${id}`
    : `/workbooks/${id},hash=${hash}`;
  return download(cadenzaUrl(url), {
    headers: {
      accept: MIMETYPE_ZIP
    }
  });
};

export const importWorkbookFromFile = (name: string,
  repositoryName: RepositoryName,
  folderItemName: string,
  labels: string[],
  { fileName, signature }: { fileName: string; signature: string },
  projectId?: ProjectIdTuple,
  dataProtectionContextId?: DataProtectionContextIdTuple,
  deletionDeadlineInfo?: DeletionDeadlineInfoDto) => {
  const formData = new FormData();
  formData.append('fileName', fileName);
  formData.append('signature', signature);
  formData.append('name', name);
  if (projectId) {
    formData.append('projectName', projectId[1]);
  }
  if (dataProtectionContextId) {
    formData.append('dataProtectionContextName', dataProtectionContextId[1]);
  }
  if (deletionDeadlineInfo?.deletionDeadline) {
    formData.append('deletionDeadline', deletionDeadlineInfo.deletionDeadline);
    deletionDeadlineInfo.emailAddresses?.forEach(emailAddress => formData.append('emailAddresses', emailAddress));
  }
  labels.forEach(label => formData.append('labels', label));
  formData.append('folderItemName', folderItemName);
  return ajax<WorkOrderId>(`/repositories/${repositoryName}/workbooks/import-workbook-work-orders`, {
    type: 'POST',
    cache: false,
    contentType: false,
    processData: false,
    data: formData,
    headers: {
      Accept: MIMETYPE_JSON
    }
  });
};

export const updateWorkbookFromFile = (workbookId: WorkbookId,
  repositoryName: RepositoryName,
  name: string,
  { fileName, signature }: { fileName: string; signature: string },
  dataProtectionContextId?: DataProtectionContextIdTuple) => {
  const formData = new FormData();
  formData.append('fileName', fileName);
  formData.append('signature', signature);
  formData.append('name', name);
  if (dataProtectionContextId) {
    formData.append('dataProtectionContextName', dataProtectionContextId[1]);
  }
  return ajax<WorkOrderId>(`/repositories/${repositoryName}/workbooks/${workbookId}/update-workbook-work-orders`, {
    type: 'PUT',
    cache: false,
    contentType: false,
    processData: false,
    data: formData,
    headers: {
      Accept: MIMETYPE_JSON
    }
  });
};

export function getCompatibleObjecttypes (workbookIdAndHash: WorkbookIdAndHash, usedInView: ViewId, dataType: string, srs: string) {
  const baseUrl = createWorkbookUrl(workbookIdAndHash) + '/objecttypes';
  const params = new URLSearchParams({ usedInView, dataType, srs });
  const url = `${baseUrl}?${params.toString()}`;
  return ajax(url, {});
}

/**
 * Get a single LOB value for the current selection of the specified workbook.
 * The selection should be exactly one LOB attribute, otherwise it will result in an error.
 *
 * @param workbookIdAndHash - The workbook
 * @return The value of the selected LOB attribute, as a string
 */
export function getSelectedLobValue (workbookIdAndHash: WorkbookIdAndHash) {
  const url = createWorkbookUrl(workbookIdAndHash) + '/selection/lob-value';
  return ajax<string>(url, { dataType: 'text' });
}

interface WorkbookLegendStructureOptions {
  workbookIdAndHash: WorkbookIdAndHash;
  layerId: string;
  mapSize: DimensionDto;
  extent: WorldBoxDto;
  srsCode: string;
}

export function getWorkbookLegendStructure ({ workbookIdAndHash, layerId: dataViewId, mapSize, extent, srsCode }: WorkbookLegendStructureOptions, signal?: AbortSignal) {
  return ajax<LegendItemDto[]>(createWorkbookUrl(workbookIdAndHash, { dataViewId: dataViewId as DataViewId }) + '/legend', {
    signal,
    headers: { Accept: MIMETYPE_JSON },
    data: {
      'WIDTH': mapSize[0],
      'HEIGHT': mapSize[1],
      'BBOX': extent.join(),
      'SRS': srsCode
    }
  });
}

export function getWorkbookSubscriptionConfig () {
  return ajax<WorkbookJobScheduleConfiguration>('/job-schedule-config');
}

export function releaseWorkbookObjecttype (objecttypeId: ObjecttypeId) {
  return ajax(`/objecttypes/${window.btoa(objecttypeId)}/parent-workbook`, { type: 'DELETE' });
}

export function getWorkbookActiveAttributeValues (workbookIdAndHash: WorkbookIdAndHash,
  activeAttributeId: ActiveAttributeId,
  filterText = '') {
  const baseUrl = createWorkbookUrl(workbookIdAndHash) + `/active-attributes/${btoa(activeAttributeId)}/table?`;
  const url = baseUrl + new URLSearchParams({ filterText });
  return ajax<TableData<AttributeValueRowDto>>(url, { dataType: 'json' });
}
